<template>
  <v-dialog transition="dialog-top-transition" max-width="800" v-model="dialogVisible" persistent>
    <v-card rounded="0">
      <v-toolbar color="primary" class="white--text" flat :elevation="0">
        <v-toolbar-title class="font-weight-bold">
          {{ customTitle || $t('deforestation.requestAdditionalInformation') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="closeModal(false)">
          <v-icon style="color: white !important;">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-form @submit.prevent="sendRequestAdditionalInformation" ref="requestAdditionalInformationForm" lazy-validation
        v-model="sendRequestAdditionalInformationData">
        <v-card-text class="pt-5">
          <v-alert v-if="batchSubtitle" type="info" dense outlined class="mb-4 text-body-2">
            {{ batchSubtitle }}
          </v-alert>

          <div v-if="isPtsiOrg && localQueuedFarms.length" class="mb-4">
            <h3 class="font-weight-medium mb-2" style="color:black">
              {{ $t('dueDiligence.farmsPendingRevisionBatch') }} ({{ localQueuedFarms.length }})
            </h3>
            <p class="text-caption text--secondary mb-3">
              {{ $t('dueDiligence.farmsPendingRevisionBatchHint') }}
            </p>
            <v-sheet outlined rounded class="pa-3" style="max-height:220px; overflow-y:auto;">
              <div
                v-for="(farm, index) in localQueuedFarms"
                :key="farm.productionPlaceId || farm.farmId || index"
                class="d-flex align-start py-2"
                :class="{ 'border-bottom': index < localQueuedFarms.length - 1 }"
              >
                <div class="flex-grow-1 pr-2">
                  <div class="text-body-2 font-weight-medium">{{ farm.farmName }}</div>
                  <div class="text-caption text--secondary">
                    {{ farm.farmerRegId || '—' }}
                    <span v-if="farm.address"> · {{ farm.address }}</span>
                  </div>
                  <v-chip x-small class="mt-1" :color="farm.requestType === 'revision' ? 'error' : 'warning'" dark>
                    {{ farm.requestType === 'revision' ? $t('dueDiligence.revisionRequired') : $t('dueDiligence.uploadRequired') }}
                  </v-chip>
                  <div v-if="farm.comment" class="text-caption mt-1">{{ farm.comment }}</div>
                </div>
                <v-btn icon x-small @click="removeLocalQueuedFarm(index)">
                  <v-icon small color="error">mdi-close</v-icon>
                </v-btn>
              </div>
            </v-sheet>
          </div>

          <div class="mb-4">
            <h3 class="font-weight-medium mb-2" style="color:black">
              {{ $t('deforestation.requestAdditionalInformationPage.descriptionLabel') }}
            </h3>
            <v-textarea
              :rules="descriptionRules"
              class="py-2"
              :rows="4"
              v-model="description"
              :placeholder="$t('Enter specific comments or instructions here')"
              outlined
              dense
              counter="1000"
              maxlength="1000"
            ></v-textarea>
          </div>

          <div class="mb-2">
            <h3 class="font-weight-medium mb-2" style="color:black">
              {{ $t('dueDiligence.attachReferenceDocuments') }}
            </h3>
            <p class="text-caption text--secondary mb-3">
              {{ $t('dueDiligence.attachReferenceDocumentsHint') }}
            </p>

            <div
              class="rai-dropzone"
              :class="{
                'rai-dropzone--active': dragging,
                'rai-dropzone--disabled': uploadingAttachments,
              }"
              @dragenter.prevent="onDragEnter"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
            >
              <div class="rai-dropzone__content">
                <v-icon size="44" :color="dragging ? 'primary' : 'grey lighten-1'">mdi-tray-arrow-up</v-icon>
                <div class="rai-dropzone__title primary--text">
                  {{ $t('deforestation.selectFileToUpload') }}
                </div>
                <div class="rai-dropzone__subtitle">
                  {{ $t('farm.dropHere') }}
                </div>
                <div class="rai-dropzone__formats text-caption text--secondary mt-2">
                  PDF, JPG, PNG, DOC, DOCX
                </div>
              </div>
              <input
                ref="fileInput"
                type="file"
                class="rai-dropzone__input"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                :disabled="uploadingAttachments"
                @change="onFileInputChange"
              />
            </div>

            <div v-if="attachmentItems.length" class="rai-attachments mt-4">
              <div class="text-subtitle-2 font-weight-medium mb-2">
                {{ $t('dueDiligence.attachedDocuments') }} ({{ attachmentItems.length }})
              </div>
              <v-row dense>
                <v-col
                  v-for="(file, index) in attachmentItems"
                  :key="file.id || file.filePath || index"
                  cols="12"
                  sm="6"
                >
                  <div class="rai-attachment-card">
                    <v-btn
                      icon
                      x-small
                      class="rai-attachment-card__remove"
                      :disabled="uploadingAttachments"
                      @click="removeAttachment(index)"
                    >
                      <v-icon small color="error">mdi-close</v-icon>
                    </v-btn>
                    <div class="rai-attachment-card__preview">
                      <span class="rai-attachment-card__ext">{{ getFileExtension(file.fileName) }}</span>
                    </div>
                    <div class="rai-attachment-card__name text-truncate">{{ file.fileName }}</div>
                    <div v-if="file.uploading" class="rai-attachment-card__status text-caption warning--text">
                      {{ $t('dueDiligence.uploading') }}...
                    </div>
                    <v-btn
                      v-else-if="file.fileUrl"
                      text
                      x-small
                      color="primary"
                      class="px-0"
                      @click="openAttachment(file.fileUrl)"
                    >
                      <v-icon x-small class="mr-1">mdi-eye</v-icon>
                      {{ $t('deforestation.view') }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>

          <v-alert v-if="errorMessage" type="error" dense outlined class="mt-2">
            {{ errorMessage }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            outlined
            class="mr-2"
            depressed
            style="min-width: 150px;"
            @click="closeModal(false)"
            color="primary"
            :disabled="uploadingAttachments"
          >
            {{ $t("cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            style="min-width: 150px;"
            type="submit"
            :loading="uploadingAttachments"
            :disabled="!isFormValid || uploadingAttachments"
          >
            {{ $t("send") }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>
<script>
import loading from "@/mixins/LoadingMixin";
import DeforestationService from "@/_services/DeforestationService";
import ActivityLogService from "@/_services/ActivityLogService";
import S3UploadService from "@/_services/S3UploadService";
import {
  resolveDdsSupplierUserId,
  resolveDdsSupplierCfUserId,
} from '@/utils/ddsMitigationNotification';
import { isIndonesiaPtsiClient } from '@/utils';

const ATTACHMENT_FOLDER = "dds-request-additional-information";
const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".doc", ".docx"];

export default {
  watch: {
    dialog: {
      handler(newVal) {
        if (newVal) {
          this.syncQueuedFarmsFromParent();
          this.resetFormFields();
          this.clearValidation();
          this.loadExistingData();
        }
      },
      immediate: true
    },
    pendingFarmRevisionRequests: {
      handler(list) {
        if (!this.dialog) return;
        this.localQueuedFarms = (list || []).map((farm) => ({ ...farm }));
      },
      deep: true,
    },
    shareAccess: {
      handler(newVal) {
        if (newVal === 'shareCompleteReport') {
          this.selectedStep = [];
        }
      }
    }
  },
  props: {
    dialog: {
      required: false,
      type: Boolean,
      default: false
    },
    supplierId: {
      required: false,
      type: [String, Number],
      default: null
    },
    visibleSelectedTabs: {
      required: false,
      type: Array,
      default: () => [],
    },
    customTitle: {
      type: String,
      default: null,
    },
    customSubtitle: {
      type: String,
      default: null,
    },
    pendingFarmRevisionRequests: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      sendRequestAdditionalInformationData: false,
      diligenceId: this.$route.params.id || null,
      description: "",
      shareAccess: "shareCompleteReport",
      selectedStep: [],
      steps: [],
      ddrId: this.$route.params.id || null,
      errorMessage: "",
      attachmentItems: [],
      uploadingAttachments: false,
      dragging: false,
      dragDepth: 0,
      localQueuedFarms: [],
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.dialog;
      },
      set(value) {
        if (!value) this.closeModal(false);
      },
    },
    isPtsiOrg() {
      return isIndonesiaPtsiClient();
    },
    batchSubtitle() {
      if (!this.isPtsiOrg) return null;
      if (this.customSubtitle && !this.localQueuedFarms.length) {
        return this.customSubtitle;
      }
      const count = this.localQueuedFarms.length;
      if (!count) {
        return this.$t('dueDiligence.requestAdditionalInformationBatchEmptyHint');
      }
      return this.$t('dueDiligence.requestAdditionalInformationBatchHint', { count });
    },
    getDiligenceDetails() {
      return this.$store.getters["eudrDDS/getDiligenceDetails"];
    },
    getLatestAdditionalInformation() {
      return this.$store.getters["eudrDDS/ddsAdditionalInformationByDiligenceIdLatest"];
    },
    descriptionRules() {
      return [
        v => !!(v && String(v).trim()) || this.$t('This field is required'),
      ];
    },
    isFormValid() {
      if (!this.description.trim()) return false;
      if (this.shareAccess === 'shareSpecificStep' && (!this.selectedStep || this.selectedStep.length === 0)) {
        return false;
      }
      return true;
    },
    resolvedSupplierId() {
      const fromProp = parseInt(this.supplierId, 10);
      if (!Number.isNaN(fromProp) && fromProp > 0) return fromProp;
      return resolveDdsSupplierUserId(this.getDiligenceDetails);
    },
    resolvedCfUserId() {
      return resolveDdsSupplierCfUserId(this.getDiligenceDetails);
    },
  },
  mounted() {
    if (this.ddrId) {
      this.getAdditionalInformationData();
    }
  },
  methods: {
    syncQueuedFarmsFromParent() {
      this.localQueuedFarms = (this.pendingFarmRevisionRequests || []).map((farm) => ({ ...farm }));
    },
    removeLocalQueuedFarm(index) {
      if (index < 0 || index >= this.localQueuedFarms.length) return;
      this.localQueuedFarms.splice(index, 1);
      this.$emit('remove-queued-farm', index);
    },
    resetFormFields() {
      this.description = "";
      this.shareAccess = "shareCompleteReport";
      this.selectedStep = [];
      this.errorMessage = "";
      this.attachmentItems = [];
      this.uploadingAttachments = false;
      this.dragging = false;
      this.dragDepth = 0;
    },
    resetData() {
      this.resetFormFields();
      this.localQueuedFarms = [];
    },
    clearValidation() {
      if (this.$refs.requestAdditionalInformationForm) {
        this.$refs.requestAdditionalInformationForm.resetValidation();
      }
    },
    closeModal(success = false) {
      const didSucceed = success === true;
      this.$emit("close-modal", didSucceed);
      this.resetData();
      this.clearValidation();
    },
    normalizeAttachmentItem(item) {
      if (!item) return null;
      return {
        id: item.id || item.filePath || item.fileName,
        fileName: item.fileName || this.getFileNameFromPath(item.filePath || item.fileUrl),
        filePath: item.filePath || '',
        fileUrl: item.fileUrl || '',
        uploading: false,
      };
    },
    getFileNameFromPath(path = '') {
      return String(path).split('/').pop() || 'document';
    },
    getFileExtension(fileName = '') {
      const parts = String(fileName).split('.');
      return parts.length > 1 ? parts.pop().toUpperCase() : 'FILE';
    },
    openAttachment(url) {
      if (url) window.open(url, '_blank');
    },
    onDragEnter() {
      if (this.uploadingAttachments) return;
      this.dragDepth += 1;
      this.dragging = true;
    },
    onDragOver() {
      if (this.uploadingAttachments) return;
      this.dragging = true;
    },
    onDragLeave() {
      if (this.uploadingAttachments) return;
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.dragging = false;
    },
    onDrop(event) {
      if (this.uploadingAttachments) return;
      this.dragging = false;
      this.dragDepth = 0;
      const files = event.dataTransfer?.files;
      if (files?.length) this.addFilesFromList(files);
    },
    onFileInputChange(event) {
      const files = event.target?.files;
      if (files?.length) this.addFilesFromList(files);
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    addFilesFromList(fileList) {
      const list = Array.from(fileList || []);
      let invalidCount = 0;

      list.forEach((file) => {
        if (!file?.name) return;
        const ext = `.${file.name.split('.').pop().toLowerCase()}`;
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
          invalidCount += 1;
          return;
        }
        const exists = this.attachmentItems.some(item => item.fileName === file.name);
        if (exists) return;
        this.attachmentItems.push({
          id: `${file.name}-${Date.now()}-${Math.random()}`,
          fileName: file.name,
          filePath: '',
          fileUrl: '',
          uploading: false,
          rawFile: file,
        });
      });

      if (invalidCount > 0) {
        this.$notify({
          text: this.$t('activationKey.incorectFileType'),
          type: 'warning',
        });
      }
    },
    removeAttachment(index) {
      this.attachmentItems.splice(index, 1);
    },
    async uploadPendingAttachments() {
      const uploaded = [];
      for (const item of this.attachmentItems) {
        if (item.filePath && item.fileUrl) {
          uploaded.push({
            fileName: item.fileName,
            filePath: item.filePath,
            fileUrl: item.fileUrl,
          });
          continue;
        }
        if (!item.rawFile) continue;
        this.$set(item, 'uploading', true);
        try {
          const result = await S3UploadService.singleUpload(item.rawFile, ATTACHMENT_FOLDER);
          uploaded.push({
            fileName: item.fileName,
            filePath: result.key,
            fileUrl: result.fullPath,
          });
        } finally {
          this.$set(item, 'uploading', false);
        }
      }
      return uploaded;
    },
    async loadExistingData() {
      if (this.ddrId) {
        await this.getAdditionalInformationData();
      }
    },
    async sendRequestAdditionalInformation() {
      this.errorMessage = "";

      if (!this.description.trim()) {
        this.errorMessage = this.$t('Description is required');
        return false;
      }

      if (!this.resolvedSupplierId) {
        this.errorMessage = this.$t('dueDiligence.supplierNotFoundForRequest');
        return false;
      }

      const data = {
        supplierId: this.resolvedSupplierId,
        description: this.description.trim(),
        dueDiligenceReportId: parseInt(this.$route.params.id, 10),
        shareAccess: this.shareAccess || "shareCompleteReport",
        cfUserId: this.resolvedCfUserId,
        isPtsiApproval: true,
      };

      this.startLoading();
      this.uploadingAttachments = true;

      try {
        if (this.attachmentItems.length) {
          data.attachments = await this.uploadPendingAttachments();
        }
        if (this.localQueuedFarms.length) {
          data.farmRevisionRequests = this.localQueuedFarms.map((farm) => ({
            productionPlaceId: farm.productionPlaceId,
            farmId: farm.farmId,
            farmName: farm.farmName,
            farmerRegId: farm.farmerRegId,
            address: farm.address,
            comment: farm.comment,
            requestType: farm.requestType,
            mitigationSourceType: farm.mitigationSourceType,
          }));
        }

        const res = await DeforestationService.requestAdditionalInformation(data);

        if (res.errors && res.errors.length > 0) {
          const error = res.errors[0];
          const errorMessage = error.message || this.$t('dueDiligence.anErrorOccured');
          this.errorMessage = errorMessage;
          return false;
        }

        if (res.data && res.data.requestAdditionalInformation) {
          const result = res.data.requestAdditionalInformation;
          if (result.success) {
            const userId = this.$store.getters.getUser?.id;

            if (userId && this.diligenceId) {
              try {
                const farmNote = data.farmRevisionRequests?.length
                  ? ` (${data.farmRevisionRequests.length} farm(s))`
                  : '';
                const attachmentNote = data.attachments?.length
                  ? ` (${data.attachments.length} attachment(s))`
                  : '';
                await ActivityLogService.createActivityLog({
                  diligenceId: parseInt(this.diligenceId, 10),
                  userId: userId,
                  activity: 'Approval Flow',
                  description: `Requested additional information${farmNote}${attachmentNote}: ${this.description.substring(0, 100)}${this.description.length > 100 ? '...' : ''}`,
                  log_type: 'approval_flow'
                });
                this.$emit('activity-log-created');
              } catch (logError) {
                console.error('Error logging additional information request:', logError);
              }
            }

            this.$notify({
              text: this.$t('Additional information request sent successfully'),
              type: 'success'
            });
            this.closeModal(true);
            return true;
          } else {
            this.errorMessage = result.message || this.$t('Failed to request additional information');
            return false;
          }
        }
      } catch (err) {
        console.error('Error requesting additional information:', err);
        this.errorMessage = err.message || this.$t('An error occurred while sending the request');
        this.$notify({
          text: this.errorMessage,
          type: "error",
        });
        return false;
      } finally {
        this.uploadingAttachments = false;
        this.stopLoading();
      }
    },
    async getAdditionalInformationData() {
      if (!this.diligenceId) return;

      this.startLoading();
      try {
        await this.$store.dispatch('eudrDDS/setDDSAdditionalInformationByDiligenceId', {
          ddrId: parseInt(this.diligenceId, 10)
        });

        if (this.getLatestAdditionalInformation) {
          this.description = this.getLatestAdditionalInformation.description || "";
          this.shareAccess = this.getLatestAdditionalInformation.shareAccess || "shareCompleteReport";

          if (this.getLatestAdditionalInformation.selectedStep) {
            try {
              const parsedSteps = JSON.parse(this.getLatestAdditionalInformation.selectedStep);
              this.selectedStep = Array.isArray(parsedSteps) ? parsedSteps : [];
            } catch (e) {
              console.warn('Error parsing selected steps:', e);
              this.selectedStep = [];
            }
          }

          const existingAttachments = this.getLatestAdditionalInformation.attachments;
          if (Array.isArray(existingAttachments)) {
            this.attachmentItems = existingAttachments
              .map(item => this.normalizeAttachmentItem(item))
              .filter(Boolean);
          }
        }
      } catch (error) {
        console.error('Error loading additional information data:', error);
        this.errorMessage = this.$t('Failed to load existing data');
      } finally {
        this.stopLoading();
      }
    },
  },
  mixins: [loading],
};
</script>

<style lang="scss" scoped>
.v-dialog {
  .v-card {
    border-radius: 8px;
  }

  .v-toolbar {
    border-radius: 8px 8px 0 0;
  }
}

.rai-dropzone {
  position: relative;
  width: 100%;
  min-height: 168px;
  border: 2px dashed #b0bec5;
  border-radius: 12px;
  background: #fafbfc;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;

  &:hover:not(.rai-dropzone--disabled) {
    border-color: $primary-color;
    background: #f3f8ff;
  }

  &--active {
    border-color: $primary-color;
    background: #e8f1ff;
    box-shadow: inset 0 0 0 1px rgba($primary-color, 0.15);
  }

  &--disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.rai-dropzone__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 28px 20px;
  pointer-events: none;
}

.rai-dropzone__title {
  margin-top: 10px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

.rai-dropzone__subtitle {
  margin-top: 4px;
  color: #757575;
  font-size: 14px;
}

.rai-dropzone__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.rai-attachment-card {
  position: relative;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fff;
  padding: 12px;
  text-align: center;
  min-height: 132px;
}

.rai-attachment-card__remove {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;
}

.rai-attachment-card__preview {
  width: 72px;
  height: 72px;
  margin: 0 auto 8px;
  border-radius: 8px;
  background: #e8f5e9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rai-attachment-card__ext {
  font-size: 13px;
  font-weight: 700;
  color: #2e7d32;
  letter-spacing: 0.5px;
}

.rai-attachment-card__name {
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  margin-bottom: 4px;
}

.rai-attachment-card__status {
  margin-top: 2px;
}

.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.v-textarea {
  .v-input__control {
    min-height: 120px;
  }
}

.v-btn {
  text-transform: none;
  font-weight: 500;

  &.v-btn--disabled {
    opacity: 0.6;
  }
}

@media (max-width: 600px) {
  .v-dialog {
    margin: 16px;
  }

  .v-card-actions {
    flex-direction: column;

    .v-btn {
      margin: 4px 0;
      width: 100%;
    }
  }
}
</style>
